import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchWorkOrderManufacturingCommandStatus } from '#/api/momMgmt/workOrder';
import {
  matchesDateRangeFilter,
  matchesMultiFilter,
  matchesTextFilter,
} from '#/modules/commonComponents/table/clientListFilterMatchers';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import MaterialChecksDialog from '#/modules/momMgmt/analysis/orders/components/MaterialChecksDialog';
import { buildWorkOrderStatusColumns } from '#/modules/momMgmt/analysis/orders/data/workOrderStatusColumns';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import { applyScheduledStartDateRangeToFilters } from '#/modules/momMgmt/workOrder/helper/scheduledStartDateListFilter';
import { workOrderManufacturingCommandStatusQueryKey } from '#/utils/queryKeys';

const EMPTY_LIST_QUERY = {
  keyword: '',
  sort: [],
  filters: {},
};

const EMPTY_WORK_ORDERS = [];

const SORT_FIELD_LABELS = {
  workOrderNo: '工單號',
  mtr: '成品',
  workOrderStatus: '工單狀態',
  materialStatus: '物料狀態',
  processingFactory: '加工廠',
  workWarehouse: '工作倉',
  scheduledStartDate: '排程日',
  originalDemandQty: '原始需求',
  finishedGoodsAllocatedQty: '成品抵扣',
  plannedProductionQty: '計劃產量',
  warnings: '警示',
};

function buildProcessingFactoryFilterOptions(workOrders) {
  const byId = new Map();
  (workOrders ?? []).forEach((row) => {
    const factory = row?.processingFactory;
    if (factory?.id == null || factory.id === '') {
      return;
    }
    const value = String(factory.id);
    if (byId.has(value)) {
      return;
    }
    byId.set(value, {
      value,
      label: factory.name ?? '',
    });
  });
  return Array.from(byId.values()).sort((left, right) => (
    String(left.label).localeCompare(String(right.label), 'zh-Hant')
  ));
}

function matchesKeyword(row, keyword) {
  if (!keyword) {
    return true;
  }
  const q = keyword.trim().toLowerCase();
  if (!q) {
    return true;
  }
  const haystack = [
    row?.workOrderNo,
    row?.mtr?.mtrNo,
    row?.mtr?.name,
    row?.processingFactory?.name,
    row?.workWarehouse?.name,
    row?.workOrderStatus,
    row?.materialStatus,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function matchesClientFilters(row, listQuery) {
  const filters = listQuery.filters || {};

  return (
    matchesKeyword(row, listQuery.keyword)
    && matchesTextFilter(row, 'workOrderNo', filters.workOrderNo)
    && matchesTextFilter(row, ['mtr.mtrNo', 'mtr.name'], filters.mtr)
    && matchesMultiFilter(row, 'workOrderStatus', filters.workOrderStatus)
    && matchesMultiFilter(row, 'materialStatus', filters.materialStatus)
    && matchesMultiFilter(row, 'processingFactory.id', filters.processingFactoryId)
    && matchesTextFilter(row, 'workWarehouse.name', filters.workWarehouse)
    && matchesDateRangeFilter(
      row,
      'scheduledStartDate',
      filters.scheduledStartDateFrom,
      filters.scheduledStartDateTo,
    )
  );
}

const WorkOrderStatusPage = () => {
  const [listQuery, setListQuery] = useState(EMPTY_LIST_QUERY);
  const [checksRow, setChecksRow] = useState(null);

  const handleMaterialStatusClick = useCallback((row) => {
    setChecksRow(row);
  }, []);

  const setScheduledStartDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      filters: applyScheduledStartDateRangeToFilters(prev.filters, range),
    }));
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'scheduledStartDateFrom' && value === undefined) {
      setScheduledStartDateRangeFilter(undefined);
      return;
    }
    setListQuery((prev) => ({
      ...prev,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, [setScheduledStartDateRangeFilter]);

  const statusQuery = useQuery({
    queryKey: workOrderManufacturingCommandStatusQueryKey(),
    queryFn: () => fetchWorkOrderManufacturingCommandStatus(),
  });

  const workOrders = useMemo(
    () => statusQuery.data?.data?.workOrders ?? EMPTY_WORK_ORDERS,
    [statusQuery.data?.data?.workOrders],
  );

  const processingFactoryOptions = useMemo(
    () => buildProcessingFactoryFilterOptions(workOrders),
    [workOrders],
  );

  const columns = useMemo(
    () => buildWorkOrderStatusColumns({
      filters: listQuery.filters || {},
      onFilterChange: handleFilterChange,
      onScheduledStartDateRangeChange: setScheduledStartDateRangeFilter,
      onMaterialStatusClick: handleMaterialStatusClick,
      processingFactoryOptions,
    }),
    [
      listQuery.filters,
      handleFilterChange,
      setScheduledStartDateRangeFilter,
      handleMaterialStatusClick,
      processingFactoryOptions,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const rows = useMemo(
    () => workOrders.filter((row) => matchesClientFilters(row, listQuery)),
    [workOrders, listQuery],
  );

  function handleSearch(keyword) {
    setListQuery((prev) => ({ ...prev, keyword: keyword ?? '' }));
  }

  function handleReset() {
    setListQuery(EMPTY_LIST_QUERY);
  }

  function handleSortingChange(sorting) {
    setListQuery((prev) => ({
      ...prev,
      sort: (sorting ?? []).map((item) => ({
        id: item.id,
        desc: Boolean(item.desc),
      })),
    }));
  }

  function handleSortRemove(index) {
    setListQuery((prev) => ({
      ...prev,
      sort: (prev.sort ?? []).filter((_, i) => i !== index),
    }));
  }

  return (
    <>
      <div className="table-panel min-h-0 flex-1">
        <ListTablePanel
          columns={columns}
          rows={rows}
          isLoading={statusQuery.isLoading}
          isError={statusQuery.isError}
          errorMessage={
            statusQuery.isError
              ? getWorkOrderApiErrorMessage(statusQuery.error, '載入列表失敗')
              : undefined
          }
          onRetry={statusQuery.isError ? () => { statusQuery.refetch(); } : undefined}
          serverSide={false}
          listQuery={listQuery}
          filterStatusFields={filterStatusFields}
          sortFieldLabels={SORT_FIELD_LABELS}
          onSearch={handleSearch}
          onReset={handleReset}
          initialKeyword={listQuery.keyword}
          searchPlaceholder="搜尋工單號、成品、倉廠或狀態"
          onKeywordClear={() => handleSearch('')}
          onFilterChange={handleFilterChange}
          onSortingChange={handleSortingChange}
          onSortRemove={handleSortRemove}
        />
      </div>

      <MaterialChecksDialog
        open={Boolean(checksRow)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setChecksRow(null);
          }
        }}
        workOrderNo={checksRow?.workOrderNo}
        materialStatus={checksRow?.materialStatus}
        materialChecks={checksRow?.materialChecks ?? []}
      />
    </>
  );
};

export default WorkOrderStatusPage;
